import { Box, Grid, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField, Tab, Tabs, Typography, useTheme, FormControl, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';
import React from 'react';
import AccessDeniedView from '../components/AccessDeniedView';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import LocalizedTextSetField from '../components/LocalizedTextField';

import { DataAPIContext } from '../api';
import { CREATE } from '../api/dataProvider';
import FixedLinearProgress from '../components/FixedLinearProgress';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { ContextStore } from '../store/contextStore';
import TabPanel, { a11yProps } from '../components/TabPanel';
import { TEMPLATE_LANGUAGE_JINJA2, TEMPLATE_LANGUAGE_DJANGO, TEMPLATE_LANGUAGES } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        overflowY: 'auto',
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 3.5rem)`,
        },
        margin: theme.spacing(2),
        maxWidth: 'lg',
    },
    recordListWrapper: {
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 15rem) !important`,
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        height: `calc(${theme.cp.contentHeight} - 11.5rem) !important`,
    },
}));

const EMPTY_STRING = {
    name: '',
    type: null,
    ruleset: null,
    html: {values: {}},
    markdown: {values: {}},
    plaintext: {values: {}},
    template_language: TEMPLATE_LANGUAGE_JINJA2,
    tour_operator: null,
};

const NotificationStringCreate = (props) => {
    const theme = useTheme();
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'notification_string';
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm, isFranchise, currentLicensee } = React.useContext(ContextStore);
    const [string, setString] = React.useState({ ...EMPTY_STRING, tour_operator: currentLicensee || null });
    const [syncActive, setSyncActive] = React.useState(false);
    const hasWritePerm = hasPerm('notification', 'write')
    const [formTouched, setFormTouched] = React.useState(false);
    const [currentFormat, setCurrentFormat] = React.useState(0);
    const [expandedLanguages, setExpandedLanguages] = React.useState(false);

    if (!hasWritePerm) {
        return <AccessDeniedView />
    }

    const onCurrentFormatChange = (e, nv) => {
        // console.log('nsedit: current format change', e, nv);
        setCurrentFormat(nv);
    }

    const onExpandedLanguagesChange = (ev) => {
        // console.log('nsedit: on expanded languages change', ev.target.checked, expandedLanguages);
        setExpandedLanguages(ev.target.checked);
        ev.preventDefault();
        ev.stopPropagation();
        return false;
    }

    const onTextChange = (e) => {
        setString((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    const onSelectChange = (e) => {
        setString((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    const onSwitchChange = (e) => {
        setString((prev) => ({ ...prev, [e.target.name]: e.target.checked }));
    }

    const onLicenseeChange = (id) => {
        setString((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    const onRulesetSelect = (e, nv) => {
        setString((prev) => ({ ...prev, ruleset: nv }));
    }

    const onTypeSelect = (e, nv) => {
        setString((prev) => ({ ...prev, type: nv }));
    }

    const validateInput = (string, isFranchise) => {
        const errors = {};
        let canSave = true;
        if (isFranchise && !string.tour_operator) {
            errors.tour_operator = { error: true, helperText: 'Licensee is required' };
            canSave = false;
        }
        if (!string.type) {
            errors.type = { error: true, helperText: 'Notification String Type is required' };
            canSave = false;
        }
        if (!string.name) {
            errors.name = { error: true, helperText: 'Name is required' };
            canSave = false;
        }
        return { canSave, errors };
    }

    const { canSave, errors: validationErrors } = validateInput(string, isFranchise);

    const onSave = () => {
        if (!string.type) {
            enqueueSnackbar('Type is a required field', { variant: 'error' });
            return;
        }
        if (!string.name) {
            enqueueSnackbar('Name is a required field', { variant: 'error' });
            return;
        }
        setSyncActive(true);
        dataAPI(CREATE, resource, { data: string }).then((res) => {
            if (res.statusCode === 201) {
                enqueueSnackbar('Notification String created', { variant: 'success' });
                history.push(`/notifications/strings/${res.data.id}`);
            } else if (res.statusCode === 406) {
                enqueueSnackbar(res.data.message, { variant: 'error' });
            } else {
                enqueueSnackbar(`Error creating Notification String (${res.statusCode})`, { variant: 'error' });
            }
        }).catch((err) => {
            enqueueSnackbar('Error creating Notification String', { variant: 'error' });
        })
        .finally(() => setSyncActive(false));
    }

    const onCancel = () => {
        history.push('/notifications/strings');
    }

    return (<div className={classes.root}>
        <FormLicenseeContext.Provider value={string.tour_operator}>
        <Grid container direction="row" spacing={2}>
            <Grid item xs={12}>
                <FixedLinearProgress visibility={syncActive} color="primary"/>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h6">Create New Notification String</Typography>
            </Grid>
            <Grid item xs={12}>
                <LicenseeFormField
                    value={string.tour_operator}
                    onChange={onLicenseeChange}
                    error={validationErrors.tour_operator?.error}
                    helperText={validationErrors.tour_operator?.helperText}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    disabled={props.disabled}
                    control={
                        <Switch name="enabled" checked={string.enabled} onChange={onSwitchChange} />
                    }
                    label="Enabled"
                />
            </Grid>
            <Grid item xs={12}>
                <FormControl variant="filled" size="small" fullWidth error={!string.type && formTouched}>
                    <RemoteAutocomplete
                        resource="notification_string_type"
                        urlSuffix={null}
                        label="Type"
                        variant="filled"
                        size="small"
                        fullWidth
                        onChange={onTypeSelect}
                        value={string.type}
                        disabled={props.disabled}
                        error={!string.type && formTouched}
                        required
                        onBlur={() => setFormTouched(true)}
                    />
                    {
                        !string.type && formTouched && <FormHelperText>{validationErrors.type.helperText}</FormHelperText>
                    }
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="name"
                    label="Name"
                    variant="filled"
                    fullWidth
                    value={string.name}
                    onChange={onTextChange}
                    disabled={props.disabled}
                    error={!string.name && formTouched}
                    onBlur={() => setFormTouched(true)}
                    helperText={!string.name && formTouched ? "Name is required" : ""}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="codename"
                    label="Code Name"
                    variant="filled"
                    fullWidth
                    value={string.codename}
                    onChange={onTextChange}
                    disabled={props.disabled}
                />
            </Grid>
            <Grid item xs={12}>
                <RemoteAutocomplete
                    getOptionLabel={opt => (opt.name || "")}
                    resource="notification_ruleset"
                    urlSuffix={null}
                    label="Ruleset"
                    variant="filled"
                    size="small"
                    fullWidth
                    onChange={onRulesetSelect}
                    value={string.ruleset}
                    disabled={props.disabled}
                />
            </Grid>
            <Grid item xs={12}>
                <Tabs value={currentFormat} onChange={onCurrentFormatChange}>
                    <Tab label="HTML" {...a11yProps(0)} />
                    <Tab label="Markdown" {...a11yProps(1)} />
                    <Tab label="Plain text" {...a11yProps(2)} />
                    <Box sx={{ marginLeft: theme.spacing(8), display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            disabled={props.disabled}
                            control={
                                <Switch
                                    size="small"
                                    checked={expandedLanguages}
                                    onChange={onExpandedLanguagesChange}
                                    />
                            }
                            label="Expand"
                        />
                    </Box>
                </Tabs>
                <TabPanel value={+currentFormat} index={0}>
                    <LocalizedTextSetField
                        expanded={expandedLanguages}
                        disabled={props.disabled}
                        variant="filled"
                        size="small"
                        label="HTML value"
                        name="html"
                        value={string.html}
                        fullWidth
                        onChange={onTextChange}
                    />
                </TabPanel>
                <TabPanel value={+currentFormat} index={1}>
                    <LocalizedTextSetField
                        expanded={expandedLanguages}
                        disabled={props.disabled}
                        variant="filled"
                        size="small"
                        label="Markdown value"
                        name="markdown"
                        value={string.markdown}
                        fullWidth
                        onChange={onTextChange}
                    />
                </TabPanel>
                <TabPanel value={+currentFormat} index={2}>
                    <LocalizedTextSetField
                        expanded={expandedLanguages}
                        disabled={props.disabled}
                        variant="filled"
                        size="small"
                        label="Plain text value"
                        name="plaintext"
                        value={string.plaintext}
                        fullWidth
                        onChange={onTextChange}
                    />
                </TabPanel>
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    disabled={props.disabled}
                    control={
                        <Switch name="is_template" checked={string.is_template} onChange={onSwitchChange} />
                    }
                    label="Value is a template"
                />                
            </Grid>
            <Grid item xs={12}>
                <FormControl variant="filled" size="small" fullWidth>
                    <InputLabel id="create-template-language-label">Template language</InputLabel>
                    <Select
                        labelId="create-template-language-label"
                        name="template_language"
                        value={string.template_language || TEMPLATE_LANGUAGE_JINJA2}
                        onChange={onSelectChange}
                        label="Template language"
                        disabled={props.disabled}
                    >
                        <MenuItem value={TEMPLATE_LANGUAGE_JINJA2}>{TEMPLATE_LANGUAGES[TEMPLATE_LANGUAGE_JINJA2]}</MenuItem>
                        <MenuItem value={TEMPLATE_LANGUAGE_DJANGO}>{TEMPLATE_LANGUAGES[TEMPLATE_LANGUAGE_DJANGO]}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <FormActionButtons
                    onSubmit={onSave}
                    submitDisabled={!hasWritePerm || !canSave}
                    onCancel={onCancel}
                    cancelDisabled={!hasWritePerm}
                    size="medium"
                />
            </Grid>
        </Grid>
        </FormLicenseeContext.Provider>
    </div>);
}

export default NotificationStringCreate;
